import type { DocNode } from '@atlaskit/adf-schema/doc';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import type {
	TwilioLiveChatMessage,
	TwilioMessageAttributes,
} from '../../controllers/chat-store/twilio-live-chat';
import type { InteractionChannel } from '../../services/assistance-service';

import type { TwilioClientTokenMeta } from './ocg-client-store';
import {
	buildConversationUrl,
	buildHelpcenterUrl,
	buildTwilioUrl,
	getJwtMetadata,
} from './ocg-client-utils';
import type { GetConversationMessagesResponse, OmniConversationsResponse } from './ocg-types';

/** BE response: POST .../access-token → { accessToken: "<JWT>" } */
type AccessTokenResponse = {
	accessToken: string;
	expiresAt?: number;
};

/**
 * Thrown by {@link fetchAccessToken} (and its public wrapper
 * {@link fetchTokenAndBuildMeta}) when the OCG `/access-token` endpoint
 * returns a non-2xx HTTP response. Carries the HTTP status so callers can
 * decide whether to retry or treat the failure as terminal.
 *
 * BEARS-1934: bootstrapping the Twilio client must NOT retry indefinitely on
 * non-2xx responses. Callers gate on `instanceof AccessTokenRequestError`
 * (or, equivalently, `error.name === 'AccessTokenRequestError'`) to mark the
 * Twilio bootstrap as fatally failed and stop further attempts.
 */
export class AccessTokenRequestError extends Error {
	public readonly status: number;

	constructor(message: string, status: number) {
		super(message);
		// Restore the prototype chain explicitly. When a class that extends a
		// built-in (such as `Error`) is transpiled to ES5 (CommonJS / older
		// browser targets), the `super()` call returns an object whose
		// prototype is `Error.prototype` rather than
		// `AccessTokenRequestError.prototype`. As a result,
		// `err instanceof AccessTokenRequestError` returns `false` even for
		// instances thrown by this constructor — see
		// https://github.com/microsoft/TypeScript/wiki/FAQ#why-doesnt-extending-built-ins-like-error-array-and-map-work
		// The BEARS-1934 fix in `getOrCreateTwilioClient` relies on
		// `error instanceof AccessTokenRequestError` to mark the Twilio
		// bootstrap as fatally failed; without this `setPrototypeOf` call the
		// gated short-circuit silently fails on older targets and the
		// endless-POST loop reappears.
		Object.setPrototypeOf(this, AccessTokenRequestError.prototype);
		this.name = 'AccessTokenRequestError';
		this.status = status;
	}
}

/**
 * Callback that resolves a fresh set of `Headers` (typically containing an
 * `Authorization` bearer token) to attach to OCG requests. Returning `null`
 * indicates that no extra headers are available — the request will be made
 * without them, and any 401 response will be surfaced unchanged.
 *
 * See {@link request} for the 401 retry behaviour.
 */
export type GetAuthHeadersFn = () => Promise<Headers | null>;

/**
 * Callback that informs the underlying auth provider that its cached
 * credentials are stale (typically because the previous request returned
 * `401 Unauthorized`) so that the next call to {@link GetAuthHeadersFn}
 * resolves a fresh token.
 *
 * Without this hook, providers that cache tokens (notably
 * `ExternalOAuthProvider`) would re-issue the same stale `Bearer` token on
 * the retry inside {@link request}, defeating the entire 401-recovery
 * mechanism.
 */
export type InvalidateAuthFn = () => void;

/**
 * Internal: perform a single `fetch` against an OCG endpoint with the standard
 * JSON Accept/Content-Type headers. Authentication  headers are merged in from
 * the optional `extraHeaders` parameter.
 *
 * Prefer {@link request} for new code — it handles 401 recovery via the
 * supplied {@link GetAuthHeadersFn}.
 */
const rawRequest = async (
	url: string,
	options: RequestInit,
	extraHeaders?: Headers | null,
): Promise<Response> => {
	const requestHeaders = new Headers({
		Accept: 'application/json',
		'Content-Type': 'application/json',
	});
	new Headers(options.headers ?? {}).forEach((headerValue, headerName) =>
		requestHeaders.set(headerName, headerValue),
	);
	extraHeaders?.forEach((headerValue, headerName) => requestHeaders.set(headerName, headerValue));
	return fetch(url, { ...options, headers: requestHeaders });
};

/**
 * Perform an OCG request with auth headers resolved on demand by a
 * {@link GetAuthHeadersFn} callback. When the initial response is
 * `401 Unauthorized`:
 *
 * 1. {@link InvalidateAuthFn} is invoked to clear any cached credentials in
 *    the underlying auth provider (critical for `ExternalOAuthProvider`,
 *    which otherwise returns the same stale `Bearer` token on the next call).
 * 2. {@link GetAuthHeadersFn} is invoked a second time to resolve the new,
 *    refreshed headers.
 * 3. The original request is retried exactly once with those headers.
 *
 * The retried response is returned regardless of its status. This
 * single-retry-with-invalidate behaviour is the central piece of BEARS-1804.
 */
const request = async (
	url: string,
	options: RequestInit,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
): Promise<Response> => {
	const initialAuthHeaders = await getAuthHeaders();
	const initialResponse = await rawRequest(url, options, initialAuthHeaders);
	if (initialResponse.status !== 401) {
		return initialResponse;
	}
	// 401: drain the body so the connection can be released, then invalidate
	// the cached credentials and retry once with freshly resolved auth
	// headers (BEARS-1804). Without `invalidateAuth()`, providers that cache
	// tokens (notably `ExternalOAuthProvider`) would re-send the same stale
	// `Bearer` token and the retry would fail again with another 401.
	try {
		await initialResponse.body?.cancel();
	} catch {
		// ignore — body cancellation is best-effort.
	}
	invalidateAuth();
	const refreshedAuthHeaders = await getAuthHeaders();
	return rawRequest(url, options, refreshedAuthHeaders);
};

/**
 * POST .../access-token?helpCenterId={helpCenterId}
 * Returns a Twilio Conversations access token for the current user.
 *
 * The `helpCenterId` is forwarded to the backend so it can scope the issued
 * token (and any related identity/permission lookups) to the help center the
 * help-seeker is interacting with. It is required — every call site is gated
 * by an upstream contract that throws if the value cannot be resolved (see
 * `initializeTwilioConversation` in `controllers/chat-store/main.ts`).
 */
const fetchAccessToken = async (
	helpCenterId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<AccessTokenResponse> => {
	const baseUrl = buildTwilioUrl('/access-token', gatewayOrigin);
	const url = `${baseUrl}?helpCenterId=${encodeURIComponent(helpCenterId)}`;
	const res = await request(url, { method: 'POST', credentials }, getAuthHeaders, invalidateAuth);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new AccessTokenRequestError(
			(body as { message?: string }).message ?? `Access token request failed: ${res.status}`,
			res.status,
		);
	}
	return res.json();
};

/**
 * GET /gateway/api/jsm/csm/api/v1/omni/twilio/conversation/agent/{agentUuid}
 * Returns an agent's public name by querying with their 'UUID', which is the `twilioFlexUsername`
 * from /gateway/api/jsm/csm/api/v1/omni/twilio/flex/users.
 */
export const fetchAgentName = async (
	agentUuid: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<string> => {
	const res = await request(
		buildTwilioUrl(`/agent/${encodeURIComponent(agentUuid)}`, gatewayOrigin),
		{ method: 'GET', credentials },
		getAuthHeaders,
		invalidateAuth,
	);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `Fetch agent name failed: ${res.status}`,
		);
	}
	return (await res.json()).publicName;
};

/**
 * Fetches a new user-level access token from the OCG and returns the token string
 * along with its expiration and identity derived from the JWT.
 *
 * @param helpCenterId  – Help-kosh customer experience ID. Forwarded to the
 *                        backend as a query parameter so the issued token is
 *                        scoped to that help center. Required — see
 *                        `initializeTwilioConversation` in
 *                        `controllers/chat-store/main.ts` for the upstream
 *                        contract.
 * @param gatewayOrigin – Optional gateway origin for OCG requests.
 * @param getAuthHeaders – Optional callback resolving auth headers; on a 401
 *                          response the callback is invoked again and the
 *                          underlying access-token request is retried once.
 */
export const fetchTokenAndBuildMeta = async (
	helpCenterId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<TwilioClientTokenMeta> => {
	const tokenResponse = await fetchAccessToken(
		helpCenterId,
		gatewayOrigin,
		getAuthHeaders,
		invalidateAuth,
		credentials,
	);
	const { expiration: jwtExpiration, identity } = getJwtMetadata(tokenResponse.accessToken);
	const expiration = tokenResponse.expiresAt ?? jwtExpiration;
	return {
		token: tokenResponse.accessToken,
		expiration,
		identity,
	};
};

export type DirectLiveChatResponse = {
	interactionId: string;
	liveChatConversationId: string;
	liveChatExternalSystem: string;
	name: string;
	createdAt: string;
};

/** Error code returned by the backend when no human agent is available. */
const NO_AGENT_AVAILABLE_ERROR = 'jcs.barista.livechat.no.agent.available';

/**
 * Thrown when the /livechat/direct endpoint returns 503 because no human
 * agent is available to handle the conversation.
 */
export class NoAgentAvailableError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'NoAgentAvailableError';
	}
}

/**
 * POST .../api/v1/omni/conversation/livechat/direct
 * Creates a direct live chat conversation (no AI involvement).
 * Requires the help-kosh customer experience ID (helpCenterId).
 */
export const createDirectLiveChatConversation = async (
	helpCenterId: string,
	firstMessage: DocNode,
	gatewayOrigin: string | undefined,
	metadata: Record<string, string> | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	/**
	 * Optional intake-channel attribution for the direct live chat conversation.
	 * Forwarded as the `channel` field in the POST body. Accepted values mirror
	 * Barista's `IntakeChannel.fromChannelKey` (`"EMBED"` | `"SUPPORT_SITE"`).
	 * When omitted, the BE records no intake channel for this conversation.
	 */
	channel?: InteractionChannel,
	credentials?: RequestCredentials,
): Promise<DirectLiveChatResponse> => {
	const res = await request(
		buildConversationUrl('/livechat/direct', gatewayOrigin),
		{
			method: 'POST',
			credentials,
			body: JSON.stringify({
				helpCenterId,
				firstMessage: JSON.stringify(firstMessage),
				...(metadata && { metadata }),
				...(channel && { channel }),
			}),
		},
		getAuthHeaders,
		invalidateAuth,
	);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		const typedBody = body as { errorMessage?: string; message?: string; errors?: string[] };

		// Detect the specific "no agent available" 503 error
		if (res.status === 503 && typedBody.errors?.includes(NO_AGENT_AVAILABLE_ERROR)) {
			throw new NoAgentAvailableError(typedBody.errorMessage ?? 'No human agent available');
		}

		throw new Error(
			typedBody.message ??
				typedBody.errorMessage ??
				`Create direct live chat conversation failed: ${res.status}`,
		);
	}
	return res.json();
};

/** Possible chat modes returned by the chat configuration endpoint. */
export type ChatMode = 'HUMAN_ONLY' | 'AI_AND_HUMAN' | 'AI_ONLY';

/** Response shape from GET .../helpcenter/{helpCenterId}/chat-configuration. */
export type ChatConfigurationResponse = {
	chatMode: ChatMode;
	supportTeamFallbackMessage?: string | null;
};

/**
 * GET .../api/v1/helpcenter/{helpCenterId}/chat-configuration
 * Returns the chat configuration for a given help center.
 * Only `chatMode` and `supportTeamFallbackMessage` are used on the client.
 */
export const getChatConfiguration = async (
	helpCenterId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<ChatConfigurationResponse> => {
	const url = buildHelpcenterUrl(
		`/${encodeURIComponent(helpCenterId)}/chat-configuration`,
		gatewayOrigin,
	);
	const res = await request(url, { method: 'GET', credentials }, getAuthHeaders, invalidateAuth);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `Get chat configuration failed: ${res.status}`,
		);
	}
	return res.json();
};

/**
 * POST .../api/v1/omni/twilio/conversation/{twilioConversationId}/close
 * Closes a Twilio live chat conversation on the backend.
 * Called when the help-seeker ends a conversation that has agents joined.
 */
export const closeTwilioConversation = async (
	twilioConversationId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<void> => {
	const res = await request(
		buildTwilioUrl(`/${encodeURIComponent(twilioConversationId)}/close`, gatewayOrigin),
		{ method: 'POST', credentials },
		getAuthHeaders,
		invalidateAuth,
	);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `Close Twilio conversation failed: ${res.status}`,
		);
	}
};

/**
 * GET .../api/v1/omni/conversation?helpCenterId={helpCenterId}
 * Lists all conversations (AI, live chat, and AI+handoff) for a help-seeker
 * within a given help center. Replaces the ConvoAI-only getConversationChannels
 * for the unified conversation list (BEARS-1220 / BEARS-1256).
 */
export type TwilioConversationDetailsResponse = {
	spaceKey: string;
	workItemKey: string;
	helpCenterSlug?: string;
	csatToken?: string;
};

/**
 * GET .../api/v1/omni/twilio/conversation/{twilioConversationSid}
 * Returns spaceKey and workItemKey used to construct the CSAT survey link.
 */
export const getTwilioConversationDetails = async (
	twilioConversationSid: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<TwilioConversationDetailsResponse> => {
	const url = buildTwilioUrl(`/${encodeURIComponent(twilioConversationSid)}`, gatewayOrigin);
	const res = await request(url, { method: 'GET', credentials }, getAuthHeaders, invalidateAuth);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `Fetch CSAT work item failed: ${res.status}`,
		);
	}
	return res.json();
};

/**
 * GET .../api/v1/omni/conversation/interactions/{interactionId}/messages
 * Returns the stored conversation messages for a given interaction from omni-core,
 * normalised to {@link TwilioLiveChatMessage} so they can be fed directly into the
 * chat store without any further mapping.
 *
 * Used when the Twilio conversation has been deleted (omniProcessed is true) and
 * the Twilio SDK can no longer retrieve messages directly.
 */
export const getConversationMessages = async (
	interactionId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<TwilioLiveChatMessage[]> => {
	const url = buildConversationUrl(
		`/interactions/${encodeURIComponent(interactionId)}/messages`,
		gatewayOrigin,
	);
	const res = await request(url, { method: 'GET', credentials }, getAuthHeaders, invalidateAuth);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `Get conversation messages failed: ${res.status}`,
		);
	}
	const data: GetConversationMessagesResponse = await res.json();
	const liveChatMessages = data.liveChat ?? [];
	const conversationSid = data.metadata?.liveChat?.id ?? '';

	return liveChatMessages.map((msg) => {
		let attributes: TwilioMessageAttributes = {};
		try {
			if (msg.attributes) {
				attributes = JSON.parse(msg.attributes) as TwilioMessageAttributes;
			}
		} catch (error) {
			logException({
				exception: error,
				name: 'TwilioMessageAttributesParsingFailed',
			});
		}

		// Map the optional CSM Twilio attachments (BE-supplied for closed
		// conversations). Defensive: missing/null array → undefined so the
		// downstream store entry has no `attachments` field at all.
		const attachments =
			msg.attachments && msg.attachments.length > 0
				? msg.attachments.map((a) => ({
						mediaSid: a.mediaSid,
						contentType: a.contentType,
						filename: a.filename,
						size: a.size,
					}))
				: undefined;

		return {
			sid: msg.messageSid,
			content: msg.messageBody ?? '',
			author: msg.author,
			dateCreated: msg.dateCreated ? new Date(msg.dateCreated) : null,
			conversationSid,
			attributes,
			...(attachments ? { attachments } : {}),
		};
	});
};

export const listOmniConversations = async (
	helpCenterId: string,
	gatewayOrigin: string | undefined,
	getAuthHeaders: GetAuthHeadersFn,
	invalidateAuth: InvalidateAuthFn,
	credentials?: RequestCredentials,
): Promise<OmniConversationsResponse> => {
	const baseUrl = buildConversationUrl('', gatewayOrigin);
	// Remove trailing slash added by buildOcgUrl for empty suffix, then append query params
	const url = `${baseUrl.replace(/\/$/, '')}?helpCenterId=${encodeURIComponent(helpCenterId)}`;
	const res = await request(url, { method: 'GET', credentials }, getAuthHeaders, invalidateAuth);
	if (!res.ok) {
		const body = await res.json().catch(() => ({}));
		throw new Error(
			(body as { message?: string }).message ?? `List omni conversations failed: ${res.status}`,
		);
	}
	return res.json();
};
